// What an account holds against its limits (PROTOCOL.md §9: `GET /api/account`'s `usage` and `limits`, and the 413/403
// `quota` refusals), in the writer's words. Shared by the account page and the app; no DOM.

import type { Usage } from '../net/account.ts';

export type { Usage };

/** The free account's limits; null for the owner (no limits). A missing field has no limit. */
export type Limits = Partial<Usage> | null;

export type UsageKind = keyof Usage;

/** The share of a limit at which the app mentions it (gently). */
export const NEARLY_FULL = 0.8;

const NUMBER = new Intl.NumberFormat('en-GB');

const MB = 1024 * 1024;
const GB = 1024 * MB;

/** Bytes as MB or GB, counted in 1,024s as the relay's limits are (a 1 GiB limit reads "1 GB"): "0 MB", "4.2 MB",
 *  "312 MB", "1.5 GB". */
export function formatBytes(n: number): string {
  const v = Math.max(0, n || 0);
  if (v >= GB) {
    const gb = v / GB;
    return `${gb >= 100 ? Math.round(gb) : Number(gb.toFixed(1))} GB`;
  }
  const mb = v / MB;
  if (v > 0 && mb < 0.1) return '0.1 MB';
  return `${mb >= 100 ? Math.round(mb) : Number(mb.toFixed(1))} MB`;
}

const NOUNS: Record<UsageKind, [string, string]> = {
  bytes: ['', ''],
  projects: ['project', 'projects'],
  readerLinks: ['reader link', 'reader links'],
  agentGrants: ['connected agent', 'connected agents'],
};

/** "3 projects", "1 reader link", "4.2 MB". */
export function amount(kind: UsageKind, n: number): string {
  if (kind === 'bytes') return formatBytes(n);
  const [one, many] = NOUNS[kind];
  return `${NUMBER.format(n)} ${n === 1 ? one : many}`;
}

export const USAGE_LABELS: Record<UsageKind, string> = {
  bytes: 'Storage',
  projects: 'Projects',
  readerLinks: 'Reader links',
  agentGrants: 'Connected agents',
};

export interface UsageRow {
  kind: UsageKind;
  label: string;
  used: number;
  /** null: no limit. */
  limit: number | null;
  /** used ÷ limit (0 when there's no limit), capped at 1. */
  fraction: number;
  /** "4.2 MB of 1 GB", "3 of 10", or "4.2 MB" with no limit. */
  text: string;
  nearlyFull: boolean;
  full: boolean;
}

export function usageRows(usage: Partial<Usage> | null | undefined, limits: Limits): UsageRow[] {
  const kinds: UsageKind[] = ['bytes', 'projects', 'readerLinks', 'agentGrants'];
  return kinds.map((kind) => {
    const used = Math.max(0, Number(usage?.[kind] ?? 0) || 0);
    const raw = limits?.[kind];
    const limit = typeof raw === 'number' && raw >= 0 ? raw : null;
    const fraction = limit === null ? 0 : limit === 0 ? 1 : Math.min(1, used / limit);
    const text =
      limit === null
        ? kind === 'bytes' ? formatBytes(used) : NUMBER.format(used)
        : kind === 'bytes' ? `${formatBytes(used)} of ${formatBytes(limit)}` : `${NUMBER.format(used)} of ${NUMBER.format(limit)}`;
    return { kind, label: USAGE_LABELS[kind], used, limit, fraction, text, nearlyFull: limit !== null && fraction >= NEARLY_FULL, full: limit !== null && used >= limit };
  });
}

/** The gentle note at 80%: null when nothing is near its limit. */
export function nearlyFullNote(usage: Partial<Usage> | null | undefined, limits: Limits): string | null {
  const rows = usageRows(usage, limits).filter((r) => r.nearlyFull);
  if (!rows.length) return null;
  const full = rows.filter((r) => r.full);
  if (full.length) {
    const r = full[0];
    return r.kind === 'bytes'
      ? `Your account is full (${r.text}). Delete old projects, or export them and remove them, to make room.`
      : `You’ve reached this account’s ${r.label.toLowerCase()} limit (${r.text}).`;
  }
  const r = rows[0];
  const what = r.kind === 'bytes' ? 'storage' : r.label.toLowerCase();
  return `You’ve used ${Math.round(r.fraction * 100)}% of your ${what} (${r.text}). Export or delete projects you’ve finished with to keep room.`;
}

/** A quota refusal's details: the relay's `{limit, used}` and, when it says, which limit. */
export interface QuotaDetail {
  limit?: number;
  used?: number;
  kind?: UsageKind;
}

const KIND_ALIASES: Record<string, UsageKind> = {
  bytes: 'bytes', storage: 'bytes', projects: 'projects', project: 'projects',
  readerLinks: 'readerLinks', readerlinks: 'readerLinks', reviews: 'readerLinks', links: 'readerLinks',
  agentGrants: 'agentGrants', grants: 'agentGrants', agents: 'agentGrants',
};

export function quotaKind(v: unknown): UsageKind | undefined {
  return typeof v === 'string' ? KIND_ALIASES[v] ?? KIND_ALIASES[v.toLowerCase()] : undefined;
}

/**
 * What the writer reads when the relay refuses something for quota. Storage (or an unnamed limit, as a save's refusal
 * is) says the account is full; the others name their limit.
 */
export function quotaMessage(d: QuotaDetail = {}, o: { saving?: boolean } = {}): string {
  const kind = d.kind ?? 'bytes';
  const has = typeof d.limit === 'number';
  if (kind === 'bytes') {
    const how = has ? ` it holds ${formatBytes(d.used ?? d.limit!)} of its ${formatBytes(d.limit!)}.` : ' it has reached its storage limit.';
    const kept = o.saving ? ' Your changes are kept on this computer and will be sent when there’s room.' : '';
    return `Your account is full:${how} Delete old projects or export and remove them.${kept}`;
  }
  const limit = has ? amount(kind, d.limit!) : `its ${USAGE_LABELS[kind].toLowerCase()} limit`;
  const advice =
    kind === 'projects' ? ' Delete old projects or export and remove them, then try again.'
      : kind === 'readerLinks' ? ' Withdraw links readers have finished with, then try again.'
        : ' Disconnect an agent you no longer use on the account page, then try again.';
  return `This account can have ${limit}, and that’s reached.${advice}`;
}

/** An API call refused for quota (`403`/`413` `quota`, with `limit` and `used` in its details), in the writer's words
 *  (`kind`: which limit the call meets); else the error's own message. */
export function errorText(e: unknown, kind?: UsageKind): string {
  const x = e as { code?: unknown; details?: { limit?: unknown; used?: unknown }; message?: unknown } | null;
  if (x && typeof x === 'object' && x.code === 'quota') {
    const d = x.details ?? {};
    return quotaMessage({ kind, ...(typeof d.limit === 'number' ? { limit: d.limit } : {}), ...(typeof d.used === 'number' ? { used: d.used } : {}) });
  }
  return e instanceof Error ? e.message : String(e);
}
